// Web Push del webchat: registro del service worker, alta de suscripción y
// gating del banner de permiso. La lógica pura es testeable (vitest) porque
// todo recibe el `window`/entry por parámetro.
//
// En iOS Safari el push sólo existe con la app instalada (iOS 16.4+): en el
// navegador `PushManager` no está y acá todo se apaga solo. En modo widget o
// iframe cross-origin también se apaga (el SW no es registrable ahí).
import { getPushConfig, savePushSubscription } from "../api/chat.js";

// Cache del config VAPID: se prefetchea al montar el banner (o en boot) para
// que el click de "Avisarme" NO tenga que hacer un round-trip de red ANTES de
// Notification.requestPermission() — en Safari/iOS y Firefox ese await
// consumiría la activación del gesto y el permiso jamás se pediría.
let cachedConfig = null;
let configPromise = null;

export function prefetchPushConfig() {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (!configPromise) {
    configPromise = getPushConfig()
      .then((cfg) => { cachedConfig = cfg; return cfg; })
      .catch(() => { configPromise = null; return null; });
  }
  return configPromise;
}

// Clave pública VAPID (base64url) → Uint8Array para pushManager.subscribe.
export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

function isCrossOriginFrame(win) {
  if (win.top === win.self) return false;
  try {
    void win.top.location.href;
    return false;
  } catch {
    return true;
  }
}

export function isPushSupported(entry, win = window) {
  if (entry?.mode === "widget") return false;
  if (isCrossOriginFrame(win)) return false;
  return Boolean(
    win.isSecureContext &&
    win.navigator?.serviceWorker &&
    win.PushManager &&
    win.Notification
  );
}

export function getNotificationPermission(win = window) {
  return win.Notification?.permission || "unsupported";
}

/**
 * ¿El navegador ya sabe que no va a dar el permiso? Notification.permission
 * puede decir "default" aunque el pedido se vaya a rechazar solo, como en una
 * ventana de incógnito de Chrome (que no deja detectarla). Se consulta la
 * Permissions API y, si hay service worker registrado, el permiso de push.
 * Ante cualquier duda devuelve false: el banner se muestra y, si falla, lo
 * explica.
 * @returns {Promise<boolean>}
 */
export async function isPushBlocked(win = window) {
  try {
    const status = await win.navigator?.permissions?.query?.({ name: "notifications" });
    if (status?.state === "denied") return true;
  } catch {
    // Permissions API sin "notifications" (Safari viejo): sin datos.
  }
  try {
    const registration = await win.navigator?.serviceWorker?.getRegistration?.();
    const state = await registration?.pushManager?.permissionState?.({ userVisibleOnly: true });
    if (state === "denied") return true;
  } catch {
    // sin registro o sin permissionState: sin datos.
  }
  return false;
}

// El banner sólo aparece cuando pedir el permiso tiene sentido: soporte real,
// permiso todavía no decidido, no descartado antes, y con handoff activo
// (es el momento en que las respuestas asíncronas importan).
export function shouldShowPushBanner({ supported, permission, dismissed, handoffActive }) {
  return Boolean(supported && permission === "default" && !dismissed && handoffActive);
}

export function registerPushServiceWorker(win = window) {
  return win.navigator.serviceWorker.register("/sw.js").catch(() => null);
}

async function getReadyRegistration(win) {
  const existing = await win.navigator.serviceWorker.getRegistration();
  if (!existing) await win.navigator.serviceWorker.register("/sw.js");
  return win.navigator.serviceWorker.ready;
}

/**
 * Flujo completo de activación (debe llamarse desde un click = gesto válido).
 * @returns {"granted"|"denied"|"dismissed"|"unsupported"|"error"}
 */
export async function enablePush(conversationId, win = window) {
  // requestPermission() DEBE ser la primera sentencia dentro del gesto: sin
  // ningún await previo, la activación del usuario sigue viva y el prompt
  // aparece también en Safari/iOS y Firefox. El config ya viene prefetcheado.
  let permission;
  try {
    permission = await win.Notification.requestPermission();
  } catch {
    return "error";
  }
  if (permission !== "granted") return permission === "denied" ? "denied" : "dismissed";

  try {
    const cfg = cachedConfig || (await prefetchPushConfig());
    if (!cfg?.enabled || !cfg.publicKey) return "unsupported";

    const registration = await getReadyRegistration(win);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(cfg.publicKey)
    });
    await savePushSubscription(conversationId, subscription.toJSON());
    return "granted";
  } catch {
    return "error";
  }
}

// La baja no se ofrece en el webchat: el único camino es revocar el permiso
// desde el navegador, y eso se limpia solo (el endpoint muere y el notifier
// borra la suscripción con el 404/410). Por eso acá no hay disablePush: sin
// superficie en la UI sería código muerto.

// En cada boot con permiso ya otorgado, re-POSTea la suscripción vigente:
// refresca conversation_id tras la rotación de conversación, revive filas
// borradas por el cleanup 404/410 y absorbe pushsubscriptionchange.
export async function syncPushSubscription(conversationId, win = window) {
  if (getNotificationPermission(win) !== "granted") return;
  const registration = await win.navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager?.getSubscription();
  if (!subscription) return;
  await savePushSubscription(conversationId, subscription.toJSON());
}
